import React from 'react';
import { ColorTheme, FontTheme, RadiusTheme, ShadowTheme } from 'types/theme';
import Icon, { IconProps } from './Icon';

/** Tailwind JIT가 소스에서 전체 클래스명을 인식하도록 리터럴 맵 사용 */
const BG_CLASS_BY_COLOR: Record<ColorTheme, string> = {
  'base-bg-default': 'bg-base-bg-default',
  'base-bg-subtle': 'bg-base-bg-subtle',
  'base-bg-strong': 'bg-base-bg-strong',
  'base-bg-focus': 'bg-base-bg-focus',
  'base-bg-lovematch-subtle': 'bg-base-bg-lovematch-subtle',
  'base-txt-primary': 'bg-base-txt-primary',
  'base-txt-secondary': 'bg-base-txt-secondary',
  'base-txt-tertiary': 'bg-base-txt-tertiary',
  'base-txt-disabled': 'bg-base-txt-disabled',
  'btn-bg-primary': 'bg-btn-bg-primary',
  'btn-bg-secondary': 'bg-btn-bg-secondary',
  'btn-bg-normal': 'bg-btn-bg-normal',
  'btn-bg-alert': 'bg-btn-bg-alert',
  'btn-bg-disabled': 'bg-btn-bg-disabled',
  'btn-bg-video-primary': 'bg-btn-bg-video-primary',
  'btn-bg-lovematch-primary': 'bg-btn-bg-lovematch-primary',
  'btn-txt-primary': 'bg-btn-txt-primary',
  'btn-txt-secondary': 'bg-btn-txt-secondary',
  'btn-txt-normal': 'bg-btn-txt-normal',
  'btn-txt-alert': 'bg-btn-txt-alert',
  'btn-txt-disabled': 'bg-btn-txt-disabled',
  'btn-txt-only': 'bg-btn-txt-only',
  'btn-txt-video-primary': 'bg-btn-txt-video-primary',
  'btn-txt-lovematch-primary': 'bg-btn-txt-lovematch-primary',
  'btn-txt-lovematch-secondary': 'bg-btn-txt-lovematch-secondary',
  black: 'bg-black',
  white: 'bg-white',
};

const TEXT_CLASS_BY_COLOR: Record<ColorTheme, string> = {
  'base-bg-default': 'text-base-bg-default',
  'base-bg-subtle': 'text-base-bg-subtle',
  'base-bg-strong': 'text-base-bg-strong',
  'base-bg-focus': 'text-base-bg-focus',
  'base-bg-lovematch-subtle': 'text-base-bg-lovematch-subtle',
  'base-txt-primary': 'text-base-txt-primary',
  'base-txt-secondary': 'text-base-txt-secondary',
  'base-txt-tertiary': 'text-base-txt-tertiary',
  'base-txt-disabled': 'text-base-txt-disabled',
  'btn-bg-primary': 'text-btn-bg-primary',
  'btn-bg-secondary': 'text-btn-bg-secondary',
  'btn-bg-normal': 'text-btn-bg-normal',
  'btn-bg-alert': 'text-btn-bg-alert',
  'btn-bg-disabled': 'text-btn-bg-disabled',
  'btn-bg-video-primary': 'text-btn-bg-video-primary',
  'btn-bg-lovematch-primary': 'text-btn-bg-lovematch-primary',
  'btn-txt-primary': 'text-btn-txt-primary',
  'btn-txt-secondary': 'text-btn-txt-secondary',
  'btn-txt-normal': 'text-btn-txt-normal',
  'btn-txt-alert': 'text-btn-txt-alert',
  'btn-txt-disabled': 'text-btn-txt-disabled',
  'btn-txt-only': 'text-btn-txt-only',
  'btn-txt-video-primary': 'text-btn-txt-video-primary',
  'btn-txt-lovematch-primary': 'text-btn-txt-lovematch-primary',
  'btn-txt-lovematch-secondary': 'text-btn-txt-lovematch-secondary',
  black: 'text-black',
  white: 'text-white',
};

/** theme RadiusTheme의 xm은 tailwind의 rounded-xs(--radius-xs)에 대응 */
const ROUNDED_CLASS_BY_RADIUS: Record<RadiusTheme, string> = {
  xm: 'rounded-xs',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
};

export interface ButtonStyle {
  text?: string;
  type?: 'outline' | 'solid' | 'text';
  color?: ColorTheme;
  fontColor?: ColorTheme;
  borderWidth?: number;
  borderRadius?: RadiusTheme;
  fontTheme?: FontTheme;
  background?: string;
  icon?: IconProps;
  trailing?: React.ReactNode;
  shadow?: ShadowTheme;
}

export interface ButtonProps {
  style: ButtonStyle;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ style, onClick, className = '' }) => {
  const {
    text,
    type = 'solid',
    color = 'btn-bg-primary',
    borderWidth = 1,
    borderRadius = 'lg',
    fontTheme = 'typography-headline-lg',
    fontColor = 'white',
    background,
    icon,
    trailing,
    shadow,
  } = style;

  const defaultClass = `flex flex-row items-center justify-center py-4 ${fontTheme} flex-1 max-h-[72px]`;
  const radiusClass = ROUNDED_CLASS_BY_RADIUS[borderRadius];
  const bgClass = background ? '' : BG_CLASS_BY_COLOR[color];
  const textClass = TEXT_CLASS_BY_COLOR[fontColor];
  const shadowClass = shadow ? shadow : '';

  // solid button
  if (type === 'solid')
    return (
      <div
        className={`${defaultClass} ${bgClass} ${radiusClass} ${textClass} ${className} ${shadowClass}`}
        onClick={onClick}
        style={{ background }}
      >
        {icon && <Icon {...icon} />}
        {text}
        {trailing}
      </div>
    );

  if (type === 'text') {
    return (
      <div className={`${defaultClass} ${className} ${textClass}`} onClick={onClick}>
        {icon && <Icon {...icon} />}
        {text}
        {trailing}
      </div>
    );
  }

  // outline button
  return (
    <div className={`${defaultClass} ${radiusClass} ${bgClass} ${textClass} ${className}`}
      onClick={onClick}
      style={{
        background,
        borderWidth: borderWidth ? `${borderWidth}px` : 'none',
        borderStyle: 'solid',
        borderColor: color,
      }}>
      {icon && <Icon {...icon} />}
      {text}
      {trailing}
    </div>
  );
};

export default Button;
